import { useState } from 'react';
import { Loader2, Trash2, Mic, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { AudioRecorder } from '../components/AudioRecorder';
import { useVoiceModels, useCreateVoiceModel, useDeleteVoiceModel } from '../hooks/useVoiceModels';
import type { VoiceModel } from '../hooks/useVoiceModels';

const MAX_SAMPLES = 25;

async function uploadSampleFile(file: File): Promise<string> {
  const memberships = await api.listMyVaultMemberships();
  if (!memberships.personalHolder) {
    throw new Error('Personal workspace not provisioned');
  }
  const doc = await api.uploadFileToVault(file, memberships.personalHolder.holderId, {
    tags: ['voice-studio', 'clone-sample'],
  });
  return doc.documentId;
}

function VoiceModelCard({ model }: { model: VoiceModel }) {
  const deleteModel = useDeleteVoiceModel();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{model.name}</CardTitle>
        {model.description && <CardDescription>{model.description}</CardDescription>}
      </CardHeader>
      <CardFooter className="pt-2">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete voice model?</AlertDialogTitle>
              <AlertDialogDescription>
                &quot;{model.name}&quot; will be permanently deleted from Fias and ElevenLabs. This
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deleteModel.mutate(model.voiceModelId)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardFooter>
    </Card>
  );
}

function CreateVoiceModelForm() {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sampleFiles, setSampleFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const createModel = useCreateVoiceModel();

  const handleRecorded = (file: File) => {
    if (sampleFiles.length >= MAX_SAMPLES) return;
    setSampleFiles((prev) => [...prev, file]);
  };

  const removeSample = (index: number) => {
    setSampleFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!name.trim() || sampleFiles.length === 0) return;
    setUploading(true);
    try {
      const fileIds = await Promise.all(sampleFiles.map(uploadSampleFile));
      await createModel.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        sampleFileIds: fileIds,
      });
      setName('');
      setDescription('');
      setSampleFiles([]);
    } finally {
      setUploading(false);
    }
  };

  const isBusy = uploading || createModel.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Voice Model
        </CardTitle>
        <CardDescription>
          Record at least one audio sample of your voice, then name the model and create it. More
          samples (up to {MAX_SAMPLES}) produce better quality cloning.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="vm-name">Voice Name *</Label>
          <Input
            id="vm-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. My Voice"
            maxLength={100}
            disabled={isBusy}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="vm-desc">Description (optional)</Label>
          <Input
            id="vm-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Calm reading voice"
            maxLength={500}
            disabled={isBusy}
          />
        </div>

        <div className="space-y-2">
          <Label>
            Audio Samples ({sampleFiles.length}/{MAX_SAMPLES})
          </Label>
          {sampleFiles.length > 0 && (
            <ul className="space-y-1 max-h-36 overflow-y-auto">
              {sampleFiles.map((f, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
                >
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Mic className="h-3.5 w-3.5" />
                    Sample {i + 1} ({(f.size / 1024).toFixed(0)} KB)
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => removeSample(i)}
                    disabled={isBusy}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {sampleFiles.length < MAX_SAMPLES && (
            <AudioRecorder onRecorded={handleRecorded} disabled={isBusy} />
          )}
        </div>

        {createModel.isError && (
          <p className="text-sm text-destructive">
            {(createModel.error as Error)?.message ?? 'Failed to create voice model.'}
          </p>
        )}

        <Button
          onClick={handleCreate}
          disabled={!name.trim() || sampleFiles.length === 0 || isBusy}
          className="w-full"
        >
          {isBusy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {uploading ? 'Uploading samples…' : 'Cloning voice…'}
            </>
          ) : (
            'Create Voice Model'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

export function VoiceModelsPage() {
  const { data: voiceModels, isLoading } = useVoiceModels();

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Voice Models</h1>
        <p className="text-muted-foreground mt-1">
          Clone your voice and use it for speech synthesis.
        </p>
      </div>

      <CreateVoiceModelForm />

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading voice models…
        </div>
      ) : voiceModels && voiceModels.length > 0 ? (
        <div>
          <h2 className="text-sm font-medium text-muted-foreground mb-3">
            Your Voice Models ({voiceModels.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {voiceModels.map((model) => (
              <VoiceModelCard key={model.voiceModelId} model={model} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
